/* eslint-disable react-hooks/exhaustive-deps */
import axios from "axios";
import moment from "moment";
import { connect } from "react-redux";
import { useState, useEffect, useCallback, useMemo } from "react";
import TableBox from "../../TableBox";
import { ucfirst } from "../../../helpers/String";
import GroupSelector from "../../GroupSelector";
import NetworksSelector from "../../NetworksSelector";
import IntervalSelector from "../../IntervalSelector";
import PerformanceGroupSelector from "../../PerformanceGroupSelector";
import PerformanceBreakdownSelector from "../../PerformanceBreakdownSelector";
import { DropdownButton, Dropdown } from "react-bootstrap";
import {
  EXPORT_REPORTS_API_URL,
  API_URL,
  FORMAT_DATE_API,
  GROUP_BY_FILTER_FORMATS,
  PAGE_SIZE,
  REPORTS_API_URL,
} from "../../../constants/Globals";
import ReportsPerformanceLineChart from "./ReportsLineChart";

function camelCaseToWords(s) {
  const result = s.replace(/([A-Z])/g, " $1");
  return result.charAt(0).toUpperCase() + result.slice(1);
}
const MetricSelector = ({ chartData, value, className, onChange }) => {
  const metricKeyToTitle = (key) => {
    return camelCaseToWords(key)
      .replace("Cpm", "CPM")
      .replace("Cta", "CTA")
      .replace(/^([a-z])|[\s_]+([a-z])/g, (_, g1, g2) =>
        (g1 || g2).toUpperCase()
      );
  };

  return (
    <div className={`standard-dropdown ${className}`}>
      <DropdownButton title={metricKeyToTitle(value)}>
        {(chartData || []).length ? (
          <>
            {Object.keys(chartData[0])
              .filter((value) => value !== "date")
              .sort((a, b) =>
                metricKeyToTitle(a).localeCompare(metricKeyToTitle(b))
              )
              .map((value) => (
                <Dropdown.Item
                  key={value}
                  className="is-capitalized"
                  onClick={() => onChange(value)}
                  as="button"
                >
                  {metricKeyToTitle(value)}
                </Dropdown.Item>
              ))}
          </>
        ) : null}
      </DropdownButton>
    </div>
  );
};

function ReportsPerformance({ allNetworks = [], groups = [] }) {
  const getFromLocalStorage = (key, defaultValue) => {
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (error) {
        console.warn(
          `Error parsing localStorage key "${key}". Returning default value.`
        );
        return defaultValue;
      }
    }
    return defaultValue;
  };

  const setInLocalStorage = (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  const [groupBy, setGroupBy] = useState(getFromLocalStorage("groupBy", "day"));
  const [breakdown, setBreakdown] = useState(
    getFromLocalStorage("breakdown", "")
  );
  const [selectedGroup, setSelectedGroup] = useState(
    getFromLocalStorage("selectedGroup", 0)
  );
  const [selectedNetworks, setSelectedNetworks] = useState(
    getFromLocalStorage("selectedNetworks", [])
  );
  const [selectedMetric1, setSelectedMetric1] = useState(
    getFromLocalStorage("selectedMetric1", "pollViews")
  );
  const [selectedMetric2, setSelectedMetric2] = useState(
    getFromLocalStorage("selectedMetric2", "votes")
  );
  const [groupNetworks, setGroupNetworks] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [tablePage, setTablePage] = useState(1);
  const [tableRowsTotal, setTableRowsTotal] = useState({});
  const [tableData, setTableData] = useState([]);
  const [tableCount, setTableCount] = useState(0);
  const [sortByColumn, setSortByColumn] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [errorMessage, setErrorMessage] = useState("");
  const [intervalDates, setIntervalDates] = useState({
    start: moment().subtract(30, "days"),
    end: moment(),
  });

  const [hasMoreData, setHasMoreData] = useState(false);

  const displayError = (message) => {
    setErrorMessage(message);
    setTimeout(() => setErrorMessage(""), 2500);
  };

  useEffect(() => {
    setInLocalStorage("groupBy", groupBy);
  }, [groupBy]);

  useEffect(() => {
    setInLocalStorage("breakdown", breakdown);
  }, [breakdown]);

  useEffect(() => {
    setInLocalStorage("selectedGroup", selectedGroup);
  }, [selectedGroup]);

  useEffect(() => {
    setInLocalStorage("selectedNetworks", selectedNetworks);
  }, [selectedNetworks]);

  useEffect(() => {
    setInLocalStorage("selectedMetric1", selectedMetric1);
  }, [selectedMetric1]);

  useEffect(() => {
    setInLocalStorage("selectedMetric2", selectedMetric2);
  }, [selectedMetric2]);

  const getReportsSummary = (params) => {
    return axios.get(getApiUrl(), { params });
  };

  const getApiUrl = useCallback((addedString = "", isExport = false) => {
    return `${isExport ? EXPORT_REPORTS_API_URL : REPORTS_API_URL}/reports/groups/clients-stat-summary?columns=votes,engagements,adImpressions,cpm,audience,contacts,pollViews,revenue,pollImpressions,contactConversionRate,ctaButtonClicks,engagementRate,voteRate,ctaButtonClickRate,impressions${addedString ? addedString : ""}`;
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      axios
        .get(`${API_URL}/admin/groups/getGroupNetworks`, {
          params: {
            groupId: selectedGroup,
            pageNumber: 1,
            perPage: 100,
            orderBy: "name",
            order: "asc",
          },
        })
        .then(({ data }) => {
          setGroupNetworks(data.data);
        });
    }
  }, [selectedGroup]);

  const createCommonApiParams = () => {
    return {
      clientIds: selectedNetworks.join(","),
      groupId: selectedGroup || "",
      groupBy: groupBy,
      fromDate: intervalDates.start.format(FORMAT_DATE_API),
      toDate: intervalDates.end.format(FORMAT_DATE_API),
    };
  };

  const fetchChartData = async () => {
    try {
      const response = await getReportsSummary({
        ...createCommonApiParams(),
        sortByColumn: "date",
        sortDirection: "asc",
      });
      if (response.errorCode) {
        displayError(response.message);
      }
      response.data.data.rows.forEach((data) => {
        data.contactConversionRate =
          parseFloat(data.contactConversionRate) * 100;
        data.voteRate = parseFloat(data.voteRate) * 100;
        data.engagementRate = parseFloat(data.engagementRate) * 100;
        data.ctaButtonClickRate = parseFloat(data.ctaButtonClickRate) * 100;
      });
      setChartData(response.data?.data?.rows || []);
    } catch (error) {}
  };

  const fetchTableData = async (loadMore = false, page = 1) => {
    try {
      const params = {
        pageSize: PAGE_SIZE.INFINITE_SCROLL,
        breakDownBy: breakdown,
        page,
        sortByColumn: sortByColumn === "clientName" ? "client" : sortByColumn,
        sortDirection,
        fetchAllStatsSum: Number(page) === 1 ? 1 : 0,
        ...createCommonApiParams(),
      };

      const response =
        breakdown === "poll"
          ? await axios.get(
              `${REPORTS_API_URL}/reports/client-debate-summary`,
              { params }
            )
          : await getReportsSummary(params);
      if (response.errorCode) {
        displayError(response.message);
        return;
      }
      if (response.data.status === 1) {
        if (breakdown !== "poll") {
          response.data.data.rows.forEach((data) => {
            data.contactConversionRate =
              parseFloat(data.contactConversionRate) * 100;
            data.voteRate = parseFloat(data.voteRate) * 100;
            data.engagementRate = parseFloat(data.engagementRate) * 100;
            data.ctaButtonClickRate = parseFloat(data.ctaButtonClickRate) * 100;
          });
        }

        const newTableData = loadMore
          ? tableData.concat(response.data.data.rows || [])
          : response.data.data.rows || [];
        if (response.data.data.rows?.length === 0) {
          setHasMoreData(false);
        } else {
          setHasMoreData(
            newTableData.length < Number(response.data.data.count)
          );
        }

        setTableData(newTableData);
        if (breakdown !== "poll") {
          if (response.data.data.allStatsSum) {
            response.data.data.allStatsSum.voteRate =
              parseFloat(response.data.data.allStatsSum.voteRate) * 100;
            response.data.data.allStatsSum.engagementRate =
              parseFloat(response.data.data.allStatsSum.engagementRate) * 100;
            response.data.data.allStatsSum.ctaButtonClickRate =
              parseFloat(response.data.data.allStatsSum.ctaButtonClickRate) *
              100;
            response.data.data.allStatsSum.contactConversionRate =
              parseFloat(response.data.data.allStatsSum.contactConversionRate) *
              100;
            setTableRowsTotal(response.data.data.allStatsSum);
          }
        } else {
          if (response.data.data.allStatsSum)
            setTableRowsTotal(response.data.data.allStatsSum);
        }

        setTableCount(response.data.data.count || 0);
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (intervalDates.start < intervalDates.end) {
      fetchTableData();
    }
  }, [
    breakdown,
    sortDirection,
    intervalDates,
    selectedNetworks,
    selectedGroup,
    groupBy,
    sortByColumn,
  ]);
  useEffect(() => {
    if (intervalDates.start < intervalDates.end) {
      fetchChartData();
    }
  }, [intervalDates, selectedGroup, selectedNetworks, groupBy]);

  const exportLink = useMemo(
    () =>
      breakdown === "poll"
        ? `${EXPORT_REPORTS_API_URL}/reports/client-debate-summary?breakDownBy=${breakdown}&fromDate=${intervalDates.start.format("YYYY-MM-DD")}&toDate=${intervalDates.end.format("YYYY-MM-DD")}&sortByColumn=${sortByColumn}&sortDirection=${sortDirection}&clientIds=${selectedNetworks.join(",")}&groupId=${selectedGroup || ""}&groupBy=${groupBy}`
        : getApiUrl(
            `&breakDownBy=${breakdown}&fromDate=${intervalDates.start.format("YYYY-MM-DD")}&toDate=${intervalDates.end.format("YYYY-MM-DD")}&page=${1}&pageSize=${PAGE_SIZE.INFINITE_SCROLL}&sortByColumn=${sortByColumn}&sortDirection=${sortDirection}&clientIds=${selectedNetworks.join(",")}&groupId=${selectedGroup || ""}&groupBy=${groupBy}`,
            true
          ),
    [
      sortByColumn,
      sortDirection,
      tablePage,
      tableCount,
      intervalDates,
      breakdown,
      selectedNetworks,
      selectedGroup,
      groupBy,
    ]
  );

  const slugify = useCallback((name) => {
    return name
      .trim()
      .split(" ")
      .map((piece, index) => (index === 0 ? piece.toLowerCase() : piece))
      .join("")
      .replace("client", "clientName")
      .replace("author", "name")
      .replace("title", "thesis")
      .replace("day", "date")
      .replace("month", "date");
  }, []);

  const columnTypes = useMemo(() => {
    const defaultColumns = [
      "Poll Views",
      "Votes",
      "Vote Rate",
      "Contacts",
      "Ad Impressions",
      "Revenue",
      "CPM",
    ];

    return []
      .concat([groupBy === "day" ? "Day" : "Month"])
      .concat(breakdown !== "" ? [ucfirst(breakdown)] : [])
      .concat(defaultColumns);
  }, [groupBy, breakdown]);

  const columnForPolls = useMemo(
    () => [
      "Day",
      "Author",
      "Title",
      "Poll Impressions",
      "Poll Views",
      "Votes",
      "Engagements",
      "Cta Button Clicks",
    ],
    []
  );

  const changeOrder = (newBreakdown) => {
    if (newBreakdown === "poll") {
      setSortByColumn("date");
    } else if (
      ![
        "day",
        "adImpressions",
        "revenue",
        "cpm",
        "contacts",
        "engagements",
        "pollImpressions",
        "votes",
      ].includes(sortByColumn)
    ) {
      setSortByColumn("date");
    }
  };

  return (
    <>
      <div className="page-headline is-flex">
        <div>
          <strong>Reports</strong>&nbsp;|&nbsp;Performance
        </div>
        <IntervalSelector
          startDate={intervalDates.start}
          endDate={intervalDates.end}
          onChange={(startDate, endDate) => {
            setTablePage(1);
            setIntervalDates({
              start: startDate,
              end: endDate,
            });
          }}
        />
      </div>

      <div className="page-selectors">
        {groups?.length > 0 && (
          <GroupSelector
            value={selectedGroup}
            onChange={(id) => {
              setSelectedGroup(id);
              setSelectedNetworks([]);
              setGroupNetworks([]);
              setTablePage(1);
            }}
          />
        )}
        <NetworksSelector
          multiple={true}
          value={selectedNetworks}
          defaultText="All Networks"
          networks={selectedGroup ? groupNetworks : allNetworks}
          onChange={(ids) => {
            setTablePage(1);
            setSelectedNetworks(ids);
          }}
        />
        <PerformanceGroupSelector
          value={groupBy}
          onChange={(value) => {
            setTablePage(1);
            setGroupBy(value);
          }}
        />
      </div>

      <div className="info-box">
        <div className="info-box-header">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 14V2H6V14M20 14C20 15.1 19.1 16 18 16H6C4.9 16 4 15.1 4 14V2C4 0.9 4.9 0 6 0H18C19.1 0 20 0.9 20 2M14 18V20H2C0.9 20 0 19.1 0 18V5H2V18M14 9H16V12H14M11 4H13V12H11M8 6H10V12H8V6Z"
              fill="#BDBDBD"
            />
          </svg>
          Compare KPI's
        </div>
        <div className="chart-controls">
          <MetricSelector
            className="color-purple"
            value={selectedMetric1}
            chartData={chartData}
            onChange={(value) => setSelectedMetric1(value)}
          />
          <span>vs.</span>
          <MetricSelector
            className="color-green"
            value={selectedMetric2}
            chartData={chartData}
            onChange={(value) => setSelectedMetric2(value)}
          />
        </div>
        <div className="chart-box">
          <ReportsPerformanceLineChart
            groupBy={groupBy}
            chartData={chartData}
            selectedMetric1={selectedMetric1}
            selectedMetric2={selectedMetric2}
          />
        </div>
      </div>

      <div className="mid-selector">
        <PerformanceBreakdownSelector
          value={breakdown}
          onChange={(value) => {
            setTablePage(1);
            setBreakdown(value);
            changeOrder(value);
          }}
        />
      </div>

      <TableBox
        name={"performanceReport" + (breakdown || "")}
        count={tableCount}
        tableData={tableData}
        sortByColumn={sortByColumn}
        hasMoreData={hasMoreData}
        tableRowsTotal={tableRowsTotal}
        sortDirection={sortDirection}
        cellTag={(rowData, columnKey) => {
          if (columnKey === "date") {
            return (
              <span className="text-button">
                {moment(rowData[columnKey].substring(0, 10)).format(
                  groupBy === "day"
                    ? GROUP_BY_FILTER_FORMATS.DATE_HALF_MONTH
                    : GROUP_BY_FILTER_FORMATS.MONTH
                )}
              </span>
            );
          }
        }}
        additionColumnTypes={
          breakdown === "poll"
            ? ["CTA Button Click Rate", "Engagement Rate", "Vote Rate"]
            : [
                "Engagements",
                "Poll Impressions",
                "Impressions",
                "Audience",
                "Contact Conversion Rate",
                "CTA Button Clicks",
                "CTA Button Click Rate",
                "Engagement Rate",
              ]
        }
        columnTypes={breakdown === "poll" ? columnForPolls : columnTypes}
        slugify={(name) => slugify(name)}
        onSortChange={(column, direction) => {
          setTablePage(1);
          setSortByColumn(column);
          setSortDirection(direction);
        }}
        exportLink={exportLink}
        onLoadMore={() => {
          const newPage = tablePage + 1;
          setTablePage(newPage);
          fetchTableData(true, newPage);
        }}
      />

      {errorMessage ? <div className="fail">{errorMessage}</div> : null}
    </>
  );
}

const mapStateToProps = (state) => ({
  allNetworks: state.networks.networksList,
  groups: state.networks.groupsList,
});

export default connect(mapStateToProps, {})(ReportsPerformance);
